import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Instagram } from "lucide-react";
import { BIZ } from "@/data/business";
import { SectionHead, EASE } from "./motion";

const REELS = [
  { src: "/reels/reel1", tag: "RV", title: "Motorhome wash & wheel shine" },
  { src: "/reels/reel2", tag: "Exterior", title: "Pickup foam bath" },
  { src: "/reels/reel3", tag: "Auto", title: "Gloss black Civic" },
  { src: "/reels/reel4", tag: "Fleet", title: "Kenworth big-rig wash" },
  { src: "/reels/reel5", tag: "Inside & Out", title: "GMC SUV, inside & out" },
];

const Reel = ({ r, i }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) v.play().catch(() => {});
      else v.pause();
    }, { threshold: 0.35 });
    io.observe(v);
    return () => io.disconnect();
  }, []);
  const toggle = () => {
    const v = ref.current;
    v.muted = !muted;
    setMuted(!muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <motion.figure
      data-testid={`reel-${i + 1}`}
      initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 1, ease: EASE, delay: i * 0.08 }}
      className={`group relative shrink-0 snap-center w-[72vw] sm:w-[44vw] lg:w-auto aspect-[9/16] overflow-hidden bg-[#0e1118] border border-white/10 ${i % 2 ? "lg:translate-y-12" : ""}`}
    >
      <video ref={ref} src={`${r.src}.mp4`} poster={`${r.src}.jpg`} muted loop playsInline preload="metadata"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07080B]/90 via-transparent to-[#07080B]/30 pointer-events-none" />
      <span className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-[0.2em] bg-[#1bb4f2] text-[#07080B] px-2 py-1">{r.tag}</span>
      <button onClick={toggle} data-testid={`reel-sound-${i + 1}`} aria-label={muted ? "Unmute" : "Mute"}
        className="absolute top-3 right-3 h-10 w-10 grid place-items-center bg-black/50 backdrop-blur border border-white/20 hover:bg-[#1bb4f2] hover:text-[#07080B] transition-colors">
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>
      <figcaption className="absolute bottom-0 inset-x-0 p-5">
        <p className="font-mono text-[11px] text-slate-300 tracking-[0.2em]">0{i + 1}</p>
        <p className="mt-1 font-display font-extrabold uppercase text-base leading-tight">{r.title}</p>
      </figcaption>
    </motion.figure>
  );
};

export const Reels = () => (
  <section id="reels" data-testid="reels-section" className="relative py-24 md:py-36 overflow-hidden">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14 md:mb-20">
      <SectionHead eyebrow="Straight from the bay" lines={["Real cars.", "Real results."]} />
      <div className="max-w-sm lg:text-right flex flex-col lg:items-end gap-5">
        <p className="text-slate-300">Our most-watched clips from Instagram. Tap the speaker for sound.</p>
        <a href={BIZ.instagram} target="_blank" rel="noreferrer" data-testid="reels-instagram-follow"
          className="group inline-flex items-center gap-3 h-14 px-6 bg-white text-[#07080B] font-semibold hover:bg-[#1bb4f2] transition-colors self-start lg:self-end">
          <Instagram className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />Follow us on Instagram
        </a>
        <span className="font-mono text-xs text-slate-400 tracking-wider">{BIZ.igHandle}</span>
      </div>
    </div>
    <div className="flex lg:grid lg:grid-cols-5 gap-3 md:gap-4 overflow-x-auto lg:overflow-visible no-scrollbar snap-x snap-mandatory px-5 md:px-10 mx-auto max-w-[1440px] lg:pb-12">
      {REELS.map((r, i) => <Reel key={r.src} r={r} i={i} />)}
    </div>
  </section>
);
